import React, { useState, useEffect } from 'react';
import { Container, Card, Button, Form, Table, Alert, Spinner, Modal, Row, Col, Image } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { getCastaways } from '../api/castaways';
import { Castaway, League } from '../types';
import TribeBadge, { getTribeColor } from './TribeBadge';
import '../App.css';

const AdminCastawayManagement: React.FC = () => {
  const [leagues, setLeagues] = useState<League[]>([]);
  const [selectedLeagueId, setSelectedLeagueId] = useState<string>('');
  const [castaways, setCastaways] = useState<Castaway[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingCastaway, setEditingCastaway] = useState<Castaway | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    occupation: '',
    tribe: '',
    imageUrl: '',
  });
  const [newCastaways, setNewCastaways] = useState<Partial<Castaway>[]>([{}]);
  const [adminSeason, setAdminSeason] = useState<number>(() => parseInt(localStorage.getItem('adminSelectedSeason') || '50'));

  const fetchLeagues = async (season: number) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/leagues/all`);
      if (!response.ok) throw new Error('Failed to fetch leagues');
      const data = await response.json();
      
      // Default to 49 for legacy data
      const filteredLeagues = (data.leagues || []).filter((l: any) => (l.season || 49) === season);
      setLeagues(filteredLeagues);
      
      if (filteredLeagues.length > 0) {
        setSelectedLeagueId(filteredLeagues[0].id);
        // fetchCastaways will be triggered by useEffect observing selectedLeagueId
      } else {
        setSelectedLeagueId('');
        setCastaways([]);
      }
    } catch (err) {
      setError('Could not load leagues.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLeagues(adminSeason);
    
    const handleSeasonChange = () => {
      const newSeason = parseInt(localStorage.getItem('adminSelectedSeason') || '49');
      setAdminSeason(newSeason);
      fetchLeagues(newSeason);
    };
    window.addEventListener('adminSeasonChanged', handleSeasonChange);
    return () => window.removeEventListener('adminSeasonChanged', handleSeasonChange);
  }, []);

  useEffect(() => {
    if (selectedLeagueId) {
      fetchCastaways(selectedLeagueId);
    }
  }, [selectedLeagueId]);

  const fetchCastaways = async (leagueId: string) => {
    setIsLoading(true);
    try {
      const data = await getCastaways(leagueId);
      setCastaways(data);
    } catch (err) {
      setError('Could not load castaways.');
      setCastaways([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleNewCastawayChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const updatedCastaways = [...newCastaways];
    updatedCastaways[index] = { ...updatedCastaways[index], [name]: value };
    setNewCastaways(updatedCastaways);
  };

  const handleAddRow = () => {
    setNewCastaways([...newCastaways, {}]);
  };

  const handleRemoveRow = (index: number) => {
    const updatedCastaways = [...newCastaways];
    updatedCastaways.splice(index, 1);
    setNewCastaways(updatedCastaways);
  };

  const handleShowModal = (castaway: Castaway | null) => {
    setEditingCastaway(castaway);
    if (castaway) {
      setFormData({
        name: castaway.name,
        age: String(castaway.age),
        occupation: castaway.occupation,
        tribe: castaway.tribe,
        imageUrl: castaway.imageUrl,
      });
    } else {
      setFormData({ name: '', age: '', occupation: '', tribe: '', imageUrl: '' });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCastaway(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const url = editingCastaway
      ? `${process.env.REACT_APP_API_URL}/api/leagues/${selectedLeagueId}/castaways/${editingCastaway.id}`
      : `${process.env.REACT_APP_API_URL}/api/leagues/${selectedLeagueId}/castaways`;
    const method = editingCastaway ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, age: Number(formData.age) }),
      });
      if (!response.ok) throw new Error('Failed to save castaway');
      fetchCastaways(selectedLeagueId);
      handleCloseModal();
    } catch (err) {
      setError('Error saving castaway.');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this castaway?')) {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/leagues/${selectedLeagueId}/castaways/${id}`, {
          method: 'DELETE',
        });
        if (!response.ok) throw new Error('Failed to delete castaway');
        fetchCastaways(selectedLeagueId);
      } catch (err) {
        setError('Error deleting castaway.');
      }
    }
  };

  const handleBulkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const castawaysToSubmit = newCastaways
      .filter(c => c.name && c.age && c.occupation && c.imageUrl)
      .map(c => ({
        ...c,
        age: parseInt(String(c.age), 10)
      }));

    if (castawaysToSubmit.length === 0) {
      setError('Please fill out at least one valid row.');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/leagues/${selectedLeagueId}/castaways`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(castawaysToSubmit),
      });
      if (!response.ok) throw new Error('Failed to bulk add castaways');
      fetchCastaways(selectedLeagueId);
      setNewCastaways([{}]);
    } catch (err) {
      setError('Error bulk adding castaways.');
    }
  };

  return (
    <Container className="mt-5 mb-5">
      <div className="mb-3">
        <Link to="/admin/dashboard" className="text-decoration-none">
          &larr; Back to Admin Home
        </Link>
      </div>
      <Card className="shadow-sm home-card">
        <Card.Header as="h4" className="card-title-custom text-center">Admin - Castaway Management (Season {adminSeason})</Card.Header>
        <Card.Body>
          {leagues.length > 1 && (
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={2}>Select League:</Form.Label>
              <Col sm={10}>
                <Form.Select value={selectedLeagueId} onChange={e => setSelectedLeagueId(e.target.value)} disabled={isLoading}>
                  {leagues.map(league => (
                    <option key={league.id} value={league.id}>{league.name}</option>
                  ))}
                </Form.Select>
              </Col>
            </Form.Group>
          )}

          {leagues.length === 1 && (
            <div className="mb-3 p-2 bg-light border rounded">
              <strong>League:</strong> {leagues[0].name}
            </div>
          )}

          {leagues.length === 0 && !isLoading && (
            <Alert variant="info">No leagues found for Season {adminSeason}. You must create a league before adding survivors.</Alert>
          )}

          {selectedLeagueId && (
            <>
              {error && <Alert variant="danger">{error}</Alert>}
              <Button onClick={() => handleShowModal(null)} className="mb-3">Add New Castaway</Button>
              
              <Card className="mb-3">
                <Card.Header>Bulk Add Castaways</Card.Header>
                <Card.Body>
                  <Form onSubmit={handleBulkSubmit}>
                    {newCastaways.map((castaway, index) => (
                      <Row key={index} className="mb-2">
                        <Col><Form.Control type="text" name="name" placeholder="Name" value={castaway.name || ''} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleNewCastawayChange(index, e)} /></Col>
                        <Col><Form.Control type="number" name="age" placeholder="Age" value={castaway.age || ''} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleNewCastawayChange(index, e)} /></Col>
                        <Col><Form.Control type="text" name="occupation" placeholder="Occupation" value={castaway.occupation || ''} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleNewCastawayChange(index, e)} /></Col>
                        <Col><Form.Control type="text" name="tribe" placeholder="Tribe" value={castaway.tribe || ''} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleNewCastawayChange(index, e)} /></Col>
                        <Col><Form.Control type="text" name="imageUrl" placeholder="Image URL" value={castaway.imageUrl || ''} onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleNewCastawayChange(index, e)} /></Col>
                        <Col xs="auto"><Button variant="danger" onClick={() => handleRemoveRow(index)}>X</Button></Col>
                      </Row>
                    ))}
                    <Button onClick={handleAddRow} className="me-2">Add Row</Button>
                    <Button variant="primary" type="submit">Submit Castaways</Button>
                  </Form>
                </Card.Body>
              </Card>

              {isLoading ? (
                <Spinner animation="border" />
              ) : (
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Image</th>
                      <th>Name</th>
                      <th>Age</th>
                      <th>Occupation</th>
                      <th>Tribe</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {castaways.map(c => {
                      const selectedLeague = leagues.find(l => l.id === selectedLeagueId);
                      return (
                        <tr key={c.id}>
                          <td>
                            <Image
                              src={c.imageUrl}
                              alt={c.name}
                              roundedCircle
                              width="50"
                              height="50"
                              referrerPolicy="no-referrer"
                              style={{
                                objectFit: 'cover',
                                border: `3px solid ${getTribeColor(c.tribe, selectedLeague?.tribes, c.tribeColor)}`
                              }}
                            />
                          </td>
                          <td><strong>{c.name}</strong></td>
                          <td>{c.age}</td>
                          <td>{c.occupation}</td>
                          <td>
                            <TribeBadge tribeName={c.tribe} tribes={selectedLeague?.tribes} tribeColor={c.tribeColor} />
                          </td>
                          <td>
                            <Button variant="warning" size="sm" onClick={() => handleShowModal(c)}>Edit</Button>
                            <Button variant="danger" size="sm" className="ms-2" onClick={() => handleDelete(c.id)}>Delete</Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </>
          )}
          <div className="text-center mt-4">
            <Link to="/admin/dashboard" className="btn btn-secondary">Back to Admin Dashboard</Link>
          </div>
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>{editingCastaway ? 'Edit' : 'Add'} Castaway</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3}>Name</Form.Label>
              <Col sm={9}><Form.Control type="text" name="name" value={formData.name} onChange={handleInputChange} required /></Col>
            </Form.Group>
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3}>Age</Form.Label>
              <Col sm={9}><Form.Control type="number" name="age" value={formData.age} onChange={handleInputChange} required /></Col>
            </Form.Group>
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3}>Occupation</Form.Label>
              <Col sm={9}><Form.Control type="text" name="occupation" value={formData.occupation} onChange={handleInputChange} required /></Col>
            </Form.Group>
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3}>Tribe</Form.Label>
              <Col sm={9}><Form.Control type="text" name="tribe" value={formData.tribe} onChange={handleInputChange} required /></Col>
            </Form.Group>
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3}>Image URL</Form.Label>
              <Col sm={9}><Form.Control type="text" name="imageUrl" value={formData.imageUrl} onChange={handleInputChange} required /></Col>
            </Form.Group>
            <Button variant="primary" type="submit">Save Changes</Button>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default AdminCastawayManagement;